export interface DateBucket {
    label: string;
    /** Inclusive upper bound for the item's metric, in days. */
    maxDays: number;
}

export interface DateGroup<T> {
    label: string;
    items: T[];
}

/**
 * Assign each item to the first bucket whose `maxDays` its metric does not
 * exceed. Anything larger than every bucket — or with an unparseable metric
 * (NaN) — falls into the last bucket, so nothing is silently dropped. Input
 * order is preserved within each group, and empty groups are omitted.
 *
 * The metric is "days" in either direction depending on the caller: e.g. days
 * since an issue was reported, or days until something is due.
 */
export function groupByDateBuckets<T>(
    items: T[],
    metricDays: (item: T) => number,
    buckets: DateBucket[]
): DateGroup<T>[] {
    if (buckets.length === 0) return [];
    const grouped: DateGroup<T>[] = buckets.map(b => ({ label: b.label, items: [] }));
    const lastIndex = buckets.length - 1;
    for (const item of items) {
        const metric = metricDays(item);
        const idx = buckets.findIndex(b => metric <= b.maxDays);
        grouped[idx === -1 ? lastIndex : idx].items.push(item);
    }
    return grouped.filter(group => group.items.length > 0);
}
